import { inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { MyHttpClientFactory, ResponseCasingEnum } from '../http';
import { FeatureSwitch } from '@ups/xplat/utils';
import { environment } from '../environments';

const RECORD_API = '/api/UserRoute/record';

interface RecordUserRouteDTO {
  applicationId: string;
  applicationVersion: string;
  domain: string;
  environment: string;
  route: string;
  parameters: string[];
  query: string;
}

export interface RouteTrackingConfig {
  applicationId: string;
  applicationVersion: string;
  applicationEnvironment: string;
  domain: string;
}

export const ROUTE_TRACKING_CONFIG = new InjectionToken<RouteTrackingConfig>(
  'RouteTrackingConfig'
);

@Injectable()
export class RouteTrackingService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly httpClient = inject(MyHttpClientFactory).createHttpClient(
    environment.urls.softwareSecurityAPI,
    true,
    ResponseCasingEnum.CamelCase
  );

  private config = inject(ROUTE_TRACKING_CONFIG);
  private subscription?: Subscription;

  constructor() {}

  enable() {
    if (!FeatureSwitch.enableNavigationTracking) {
      return;
    }

    if (
      this.config.domain === 'localhost' ||
      this.config.domain === '127.0.0.1'
    ) {
      // lets not track localhost
      return;
    }

    this.subscription ??= this.router.events
      .pipe(filter((x) => x instanceof NavigationEnd))
      .subscribe((x) => {
        try {
          this.onNavigationEnd(x);
        } catch (err) {
          console.warn('Error while submiting navigation', err);
        }
      });
  }

  disable() {
    this.subscription?.unsubscribe();
  }

  private parseRoute(navigation: NavigationEnd, route: ActivatedRoute) {
    const pathStack = [];
    const pathStackTemplate = [];
    const paramStack = [];

    for (;;) {
      pathStack.push(
        ...route.snapshot.url.map((y) => encodeURIComponent(y.path))
      );

      if (route.routeConfig?.path) {
        pathStackTemplate.push(route.routeConfig.path);

        const matches = route.routeConfig.path.matchAll(/:([a-zA-Z0-9\-_]+)/g);

        for (const param of matches) {
          // when overriden by ;param=value, you can find the original param here,
          // but only when its overriden???
          let value = route.snapshot.url.find((y) => y.parameters[param[1]]);
          // not overriden
          value ??= route.snapshot.params[param[1]];

          paramStack.push(value);
        }
      }

      if (!route.firstChild) break;
      route = route.firstChild;
    }

    const templatePath = pathStackTemplate.join('/');
    const path = pathStack.join('/');
    const queryParams = navigation.url.match(/([?;#].+)$/)?.[1] ?? '';

    return {
      path: '/' + path,
      templatePath: '/' + templatePath,
      paramStack: paramStack,
      queryParams: queryParams,
    };
  }

  private recordNavigation(data: RecordUserRouteDTO) {
    return this.httpClient.post(RECORD_API, data);
  }

  private onNavigationEnd(event: NavigationEnd) {
    const routeInfo = this.parseRoute(event, this.route);

    this.recordNavigation({
      applicationId: this.config.applicationId,
      applicationVersion: this.config.applicationVersion,
      domain: this.config.domain,
      environment: this.config.applicationEnvironment,
      route: routeInfo.templatePath,
      parameters: routeInfo.paramStack,
      query: routeInfo.queryParams,
    }).subscribe({ error: () => ({}) });
  }
}
